import type { JsonObject } from '@/utils/json';

/**
 * A Pipelines (CDP) source sync: a scheduled import from a connector source
 * (Salesforce, HubSpot) or a database source, plus its runs and the connector's
 * record types. Served by the CDP backend, which has no OpenAPI spec, so the
 * client is hand-written (see `client.ts`).
 */

export interface SourceSync {
  id: number;
  source_id: number;
  type: string;
  name: string;
  description: string;
  enabled: boolean;
  mapping: ConnectorSourceRecordMapping;
  sync_interval: number;
  sync_timeout: number;
  sync_attempts: number;
  start_at?: number;
  last_run_at?: number;
  next_run_at?: number;
  enabled_at?: number;
  enabled_by: number;
  created_at: number;
  created_by: number;
  updated_at: number;
  updated_by: number;
}

export interface SourceSyncRun {
  id: number;
  source_sync_id: number;
  status: string;
  progress: number;
  processed_rows: number;
  /** Rows in the run. Older runs can report null. */
  total_rows: number | null;
  suppressed_rows: number;
  failed_rows: number;
  error?: string;
  started_at?: number;
  ended_at?: number;
  created_at: string | number;
  updated_at: string | number;
}

export interface CreateOrUpdateSourceSyncCmd {
  name: string;
  sync_interval: number;
  enabled: boolean;
  mapping: ConnectorSourceRecordMapping;
  config?: ConnectorSourceStreamConfig;
  start_at?: number | null;
}

export interface ConnectorSourceStreamConfig {
  supported_sync_modes: string[];
  source_defined_cursor: boolean;
  default_cursor_field: string[];
  source_defined_primary_key: string[][];
}

export interface ConnectorSourceRecord {
  name: string;
  label: string;
  labelPlural: string;
  fields: ConnectorSourceRecordField[];
  associateEntityType?: string;
  associateParentEntity?: string;
  config?: ConnectorSourceStreamConfig;
  recordTypes?: Array<{ name: string; id: string }>;
}

export interface ConnectorSourceRecordField {
  name: string;
  label: string;
  type: string;
  referenceTo?: string[];
}

export const ConnectorSourceTransformationType = {
  IDENTIFY: 'identify',
  GROUP: 'group',
  TRACK: 'track',
  RELATIONSHIP: 'relationship',
} as const;
export type ConnectorSourceTransformationType =
  (typeof ConnectorSourceTransformationType)[keyof typeof ConnectorSourceTransformationType];

export interface ConnectorSourceRecordMapping {
  record_type?: string;
  cdp_type?: ConnectorSourceTransformationType;
  identifiers?: {
    user_id?: string;
    group_id?: string;
    event?: string;
    object_name?: string;
  };
  fields?: string[];
  renames?: Record<string, string>;
  where_clause?: string;
  filters?: Record<string, Array<{ field: string; operator: string; value: string }>>;
  // Relationship syncs only: the People/Account sync ids linked at Enable
  // time by the paved-road drawer. Guards deleting/disabling those parents
  // while this sync is enabled. Frontend-owned — the API stores mapping as
  // raw JSON and the sync runner ignores unknown keys.
  linked_sync_ids?: number[];
}

export interface SourceSyncPreviewIdentifiers {
  object_name?: string;
  event?: string;
  user_id?: string;
  group_id?: string;
}

export interface SourceSyncPreviewRequest {
  object_type: string;
  cdp_type: string;
  identifiers?: SourceSyncPreviewIdentifiers;
  fields: string[];
  renames?: Record<string, string>;
  where_clause?: string;
  limit?: number;
}

export interface SourceSyncPreviewResponse {
  object_type: string;
  sample_data: JsonObject[];
  raw_data: JsonObject[];
  total_count: number;
  excluded_count?: number;
}

/** What CDP says about a Salesforce SOQL `WHERE` clause. */
export interface WhereClauseValidation {
  valid?: boolean;
  error?: string;
  /** The SOQL query CDP built from the clause. */
  query?: string;
  record_count?: number;
  /** Relationship syncs only: the distinct people and objects the clause matches. */
  distinct_users?: number;
  distinct_objects?: number;
  object_type_name?: string;
}

/** Filters for a source's sync runs. `syncImportId` narrows to one sync. */
export interface SourceSyncRunsParams {
  limit?: number;
  page?: number;
  showOnlyErrors?: boolean;
  syncImportId?: number;
  excludeEmptyRows?: boolean;
}

export const cdpSourceSyncKeys = {
  all: (workspaceId: number) => ['workspace', workspaceId, 'cdp-source-sync'] as const,
  lists: (workspaceId: number) => [...cdpSourceSyncKeys.all(workspaceId), 'list'] as const,
  /** `sourceId: null` is every source's syncs. */
  list: (workspaceId: number, params: { sourceId: number | 'journeys' | null }) =>
    [...cdpSourceSyncKeys.lists(workspaceId), params] as const,
  details: (workspaceId: number) => [...cdpSourceSyncKeys.all(workspaceId), 'detail'] as const,
  detail: (workspaceId: number, syncId: number) => [...cdpSourceSyncKeys.details(workspaceId), syncId] as const,
  /** A source's runs across its syncs, one page per params. */
  runs: (workspaceId: number, sourceId: number, params: SourceSyncRunsParams) =>
    [...cdpSourceSyncKeys.all(workspaceId), 'runs', sourceId, params] as const,
  runErrorFileUrl: (workspaceId: number, sourceId: number, runId: number) =>
    [...cdpSourceSyncKeys.all(workspaceId), 'runs', sourceId, 'error-file-url', runId] as const,
  /** A connector source's record types, as the provider describes them. */
  records: (workspaceId: number, sourceId: number) =>
    [...cdpSourceSyncKeys.all(workspaceId), 'records', sourceId] as const,
  record: (workspaceId: number, sourceId: number, record: string, includeRelationships: boolean) =>
    [...cdpSourceSyncKeys.records(workspaceId, sourceId), record, { includeRelationships }] as const,
  preview: (workspaceId: number, sourceId: number | 'journeys', request: SourceSyncPreviewRequest) =>
    [...cdpSourceSyncKeys.all(workspaceId), 'preview', sourceId, request] as const,
  /** The key an inactive read parks on. Outside the workspace namespace, and never written. */
  inactive: (read: string) => ['cdp-source-sync', 'inactive', read] as const,
};
